import {
  BackComponent,
  FormInput,
  FormLocation,
  FormSelect,
  Option,
  PageMainContent,
  Typography,
} from '@/components'
import { FormProvider } from '@/providers'
import { paths } from '@/routes/paths'
import { useCreateLocation } from '@/services'
import { UserProfile } from '@/types/user-profile'
import { useGoBack, useGoToPath } from '@/utils'
import { Button } from '@surein/ui'
import { path, pipe } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import { PageActions, PageTitle } from './styles'
import { validationSchema } from './validationSchema'

interface OutletContextType {
  user: UserProfile
}

interface FormValues {
  companyId: string
  name: string
  address: {
    addressId?: number
    city: string
    country: string
    postalCode: string
    street: string
    streetNr: string
  }
}

export const AddLocationPage = () => {
  const { t } = useTranslation()
  const { companyId = '' } = useParams()
  const { user } = useOutletContext<OutletContextType>()

  const createLocationMutation = useCreateLocation(companyId)
  const goToLocation = useGoToPath(paths.viewLocationWithKey)
  const handleBack = useGoBack()
  const handleSubmit = (values: FormValues) =>
    createLocationMutation
      .mutateAsync(values)
      .then(pipe(path(['item', 'locationId']), goToLocation))

  return (
    <>
      <PageMainContent size="sm">
        <BackComponent />
        <PageTitle>
          <Typography variant="h4">{t('add_new_location')}</Typography>
        </PageTitle>
        <FormProvider
          initialValues={{ companyId }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema(
            t('validations', { returnObjects: true }),
          )}
        >
          {!companyId && (
            <FormSelect label={t('companyPage.selectCompany')} name="companyId">
              {user.companies.map((el) => (
                <Option key={el.companyId} value={el.companyId}>
                  {el.name}
                </Option>
              ))}
            </FormSelect>
          )}
          <FormInput label={t('locationPage.locationName')} name="name" />
          <FormLocation name="address" />
          <PageActions>
            <Button
              onClick={handleBack}
              disabled={createLocationMutation.isLoading}
              variant="muted"
              shape="square"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={createLocationMutation.isLoading}
              variant="primary"
              shape="square"
            >
              {t('common.save')}
            </Button>
          </PageActions>
        </FormProvider>
      </PageMainContent>
    </>
  )
}
